import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import './TrackingService.css';

// ========== AUTHENTIFICATION ADMIN — SHA-256 ==========
const ADMIN_HASH = process.env.REACT_APP_ADMIN_HASH || '';

const hashCode = async (plainText) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plainText.trim());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// ========== API BACKEND ==========
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// ========== MISE À JOUR PROGRESSION (BACKEND) ==========
const updateProgressOnBackend = async (tripId, progress, playing, startedAt) => {
  try {
    await fetch(`${API_URL}/trips/${tripId}/progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ progress, isPlaying: playing, startedAt })
    });
  } catch (error) {
    console.error('❌ Erreur mise à jour progression:', error);
  }
};

const clamp01 = (v) => Math.min(1, Math.max(0, v));

// ========== FONCTIONS POUR LES HORAIRES ==========
const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

const calculateWaypointTimelines = (waypoints) => {
  if (!waypoints || waypoints.length < 2) return { timelines: [], totalDuration: 0 };
  
  const startMinutes = timeToMinutes(waypoints[0].time);
  let previousMinutes = startMinutes;
  const timelines = [];
  
  for (let i = 0; i < waypoints.length; i++) {
    const wp = waypoints[i];
    let currentMinutes = timeToMinutes(wp.time);
    
    if (i > 0 && currentMinutes < previousMinutes) {
      currentMinutes += 24 * 60;
    }
    
    const offsetFromStart = currentMinutes - startMinutes;
    timelines.push({
      ...wp,
      minutesFromStart: offsetFromStart >= 0 ? offsetFromStart : offsetFromStart + 24 * 60,
      rawMinutes: currentMinutes
    });
    
    previousMinutes = currentMinutes;
  }
  
  const totalDuration = timelines[timelines.length - 1].minutesFromStart;
  const waypointsWithProgress = timelines.map(wp => ({
    ...wp,
    progressThreshold: wp.minutesFromStart / totalDuration
  }));
  
  return { waypointsWithProgress, totalDuration };
};

const calculateSegmentSpeeds = (waypointsWithProgress, totalDurationSeconds) => {
  const segments = [];
  for (let i = 0; i < waypointsWithProgress.length - 1; i++) {
    const from = waypointsWithProgress[i];
    const to = waypointsWithProgress[i + 1];
    const segmentDurationMinutes = to.minutesFromStart - from.minutesFromStart;
    const segmentDurationSeconds = segmentDurationMinutes * 60;
    const segmentProgressRange = to.progressThreshold - from.progressThreshold;
    const speed = segmentProgressRange / (segmentDurationSeconds * 60);
    segments.push({
      fromProgress: from.progressThreshold,
      toProgress: to.progressThreshold,
      speed: speed,
      durationSeconds: segmentDurationSeconds,
      fromWaypoint: from,
      toWaypoint: to
    });
  }
  return segments;
};

// ========== DESSIN DU CHIEN VECTORIEL ==========
const drawDog = (ctx, cx, cy, flip = true) => {
  ctx.save();
  ctx.translate(cx, cy);
  if (flip) ctx.scale(-1, 1);

  const s = 1.15;
  ctx.scale(s, s);

  ctx.beginPath();
  ctx.arc(0, 0, 17, 0, 2 * Math.PI);
  ctx.fillStyle = 'rgba(29,158,117,0.2)';
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(-1, 3, 10, 6, -0.15, 0, 2 * Math.PI);
  ctx.fillStyle = '#C8832A';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(-11, -3, 7, 0, 2 * Math.PI);
  ctx.fillStyle = '#C8832A';
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(-16, -1, 3.5, 2.5, 0, 0, 2 * Math.PI);
  ctx.fillStyle = '#A0622A';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(-18, -2, 1.4, 0, 2 * Math.PI);
  ctx.fillStyle = '#111';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(-10, -5.5, 1.8, 0, 2 * Math.PI);
  ctx.fillStyle = '#111';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(-9.3, -6.2, 0.6, 0, 2 * Math.PI);
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(-8, -9, 3.5, 5, -0.4, 0, 2 * Math.PI);
  ctx.fillStyle = '#9B5E1A';
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(8, 0);
  ctx.quadraticCurveTo(15, -7, 11, -14);
  ctx.lineWidth = 3.5;
  ctx.strokeStyle = '#9B5E1A';
  ctx.lineCap = 'round';
  ctx.stroke();

  ctx.lineWidth = 2.8;
  ctx.strokeStyle = '#A0622A';
  ctx.lineCap = 'round';
  [[-7, 9, -8, 16], [-2, 9, -2, 16], [3, 9, 4, 16], [7, 8, 8, 15]].forEach(([x1, y1, x2, y2]) => {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  });

  ctx.restore();
};

// ========== DESSIN DU CANVAS ==========
const drawMapOnCanvas = (canvas, ship, progress) => {
  if (!canvas || !ship) return;
  const wps = ship.waypoints;
  const ctx = canvas.getContext('2d');
  const w = canvas.width, h = canvas.height;
  if (w === 0 || h === 0) return;

  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = '#f0efe7';
  ctx.fillRect(0, 0, w, h);

  const n = wps.length - 1;
  const margin = 60;
  const usableWidth = w - 2 * margin;

  // Positions proportionnelles a la duree de chaque segment
  const startMinutes = timeToMinutes(wps[0].time);
  let prevMin = startMinutes;
  const absMinutes = wps.map((wp, i) => {
    let m = timeToMinutes(wp.time);
    if (i > 0 && m < prevMin) m += 24 * 60;
    prevMin = m;
    return m;
  });
  const totalDurationMin = absMinutes[n] - absMinutes[0];

  const positions = absMinutes.map(m =>
    totalDurationMin > 0
      ? margin + ((m - absMinutes[0]) / totalDurationMin) * usableWidth
      : margin + (absMinutes.indexOf(m) / Math.max(n, 1)) * usableWidth
  );

  const startX = positions[0];
  const endX = positions[n];

  // Position X du chien selon la progression reelle (deja proportionnelle au temps)
  let currentX;
  if (progress >= 1) {
    currentX = endX;
  } else if (progress <= 0) {
    currentX = startX;
  } else {
    // Seuils de progression de chaque waypoint
    const progressThresholds = absMinutes.map(m =>
      totalDurationMin > 0 ? (m - absMinutes[0]) / totalDurationMin : 0
    );
    // Trouver le segment actif
    let segIndex = n - 1;
    for (let i = 0; i < n; i++) {
      if (progress >= progressThresholds[i] && progress < progressThresholds[i + 1]) {
        segIndex = i;
        break;
      }
    }
    const segFrom = progressThresholds[segIndex];
    const segTo = progressThresholds[Math.min(segIndex + 1, n)];
    const segFraction = segTo > segFrom ? (progress - segFrom) / (segTo - segFrom) : 0;
    currentX = positions[segIndex] + (positions[Math.min(segIndex + 1, n)] - positions[segIndex]) * segFraction;
  }

  ctx.beginPath();
  ctx.moveTo(positions[0], h / 2);
  positions.slice(1).forEach(x => ctx.lineTo(x, h / 2));
  ctx.strokeStyle = '#ddd';
  ctx.lineWidth = 4;
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(positions[0], h / 2);
  for (let i = 1; i < positions.length; i++) {
    if (positions[i] <= currentX) ctx.lineTo(positions[i], h / 2);
    else { ctx.lineTo(currentX, h / 2); break; }
  }
  ctx.strokeStyle = '#1D9E75';
  ctx.lineWidth = 4;
  ctx.stroke();

  positions.forEach((x, i) => {
    const color = wps[i].type === 'start' ? '#1D9E75' : wps[i].type === 'end' ? '#D85A30' : '#378ADD';
    ctx.beginPath();
    ctx.arc(x, h / 2, 8, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();

    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    if (wps[i].type === 'start') ctx.fillText('🚩', x, h / 2 + 4);
    else if (wps[i].type === 'end') ctx.fillText('🏁', x, h / 2 + 4);
    else ctx.fillText('📍', x, h / 2 + 4);

    ctx.fillStyle = '#333';
    ctx.font = '10px sans-serif';
    const label = wps[i].name.length > 12 ? wps[i].name.slice(0, 10) + '…' : wps[i].name;
    ctx.fillText(label, x, h / 2 - 14);
    ctx.fillStyle = '#888';
    ctx.font = '9px monospace';
    ctx.fillText(wps[i].time, x, h / 2 - 4);
  });

  const dogY = h / 2 - 22;
  if (progress >= 1) {
    drawDog(ctx, endX, dogY, true);
  } else if (progress > 0) {
    drawDog(ctx, currentX, dogY, true);
  } else {
    drawDog(ctx, startX, dogY, true);
  }
};

// ========== COMPOSANT PRINCIPAL ==========
const TrackingService = () => {
  const [activeTab, setActiveTab] = useState('track');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);

  const [activeShip, setActiveShip] = useState(null);
  const [sharedProgress, setSharedProgress] = useState(0);

  const [clientSearched, setClientSearched] = useState(false);
  const [searchCode, setSearchCode] = useState('');
  const [searchError, setSearchError] = useState('');
  const [showClientResult, setShowClientResult] = useState(false);

  const [operatorSearchCode, setOperatorSearchCode] = useState('');
  const [operatorSearchError, setOperatorSearchError] = useState('');
  const [operatorSearchResult, setOperatorSearchResult] = useState(null);
  const [operatorProgress, setOperatorProgress] = useState(0);

  const [trips, setTrips] = useState({});
  const [tripsProgress, setTripsProgress] = useState({});
  const [tripsSegments, setTripsSegments] = useState({});
  const [stopsArray, setStopsArray] = useState([]);
  const [stopName, setStopName] = useState('');
  const [stopTime, setStopTime] = useState('12:00');

  const [opForm, setOpForm] = useState({
    name: '', species: 'Chien', owner: '',
    date: new Date().toISOString().slice(0, 10),
    start: '', startTime: '08:00', end: '', endTime: '18:00',
  });

  const [generatedCode, setGeneratedCode] = useState('');
  const [showGenBox, setShowGenBox] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const animationRef = useRef(null);
  const clientRafRef = useRef(null);
  const opSearchRafRef = useRef(null);
  const canvasOpRef = useRef(null);
  const canvasClRef = useRef(null);
  const canvasOperatorSearchRef = useRef(null);

  const tripsRef = useRef(trips);
  useEffect(() => { tripsRef.current = trips; }, [trips]);
  const tripsProgressRef = useRef(tripsProgress);
  useEffect(() => { tripsProgressRef.current = tripsProgress; }, [tripsProgress]);
  const activeShipRef = useRef(activeShip);
  useEffect(() => { activeShipRef.current = activeShip; }, [activeShip]);
  const operatorSearchResultRef = useRef(operatorSearchResult);
  useEffect(() => { operatorSearchResultRef.current = operatorSearchResult; }, [operatorSearchResult]);

  // ========== FONCTIONS API BACKEND ==========
  const saveToBackend = async (tripsData, progressData, segmentsData) => {
    try {
      const response = await fetch(`${API_URL}/trips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trips: tripsData,
          tripsProgress: progressData,
          tripsSegments: segmentsData
        })
      });
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur sauvegarde backend:', error);
      return null;
    }
  };

  const loadFromBackend = async () => {
    setIsSyncing(true);
    try {
      const response = await fetch(`${API_URL}/trips`);
      const data = await response.json();
      if (data && data.trips) {
        applyBackendData(data);
        console.log('✅ Données chargées depuis le backend');
      }
    } catch (error) {
      console.error('❌ Erreur chargement backend:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const deleteTripOnBackend = async (tripId) => {
    try {
      await fetch(`${API_URL}/trips/${tripId}`, { method: 'DELETE' });
    } catch (error) {
      console.error('❌ Erreur suppression backend:', error);
    }
  };

  // ========== CHARGEMENT INITIAL DEPUIS BACKEND ==========
  useEffect(() => {
    loadFromBackend();
  }, []);

  // ========== SAUVEGARDE AUTOMATIQUE ==========
  useEffect(() => {
    const saveData = async () => {
      if (Object.keys(trips).length > 0) {
        await saveToBackend(trips, tripsProgress, tripsSegments);
      }
    };
    const timeout = setTimeout(saveData, 1000);
    return () => clearTimeout(timeout);
  }, [trips, tripsProgress, tripsSegments]);

  // ========== APPLIQUER LES DONNÉES DU BACKEND ==========
  // Met à jour le state ET les refs immédiatement (évite un calcul sur des données périmées)
  const applyBackendData = (data) => {
    tripsRef.current = data.trips || {};
    tripsProgressRef.current = data.tripsProgress || {};
    setTrips(data.trips || {});
    setTripsProgress(data.tripsProgress || {});
    setTripsSegments(data.tripsSegments || {});
  };

  // ========== SYNCHRONISATION TEMPS RÉEL (SSE) ==========
  // Le serveur diffuse chaque Animer / Pause / Réinitialiser : toutes les vues se mettent à jour
  useEffect(() => {
    if (typeof EventSource === 'undefined') return;
    const baseUrl = API_URL.replace(/\/api\/?$/, '');
    let es;
    try {
      es = new EventSource(`${baseUrl}/events`);
    } catch (e) {
      console.warn('SSE indisponible:', e);
      return;
    }
    es.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }
      if (msg.type === 'progress_update' && msg.tripId) {
        const meta = {
          progressAtStart: Number(msg.progressAtStart) || 0,
          isPlaying: msg.isPlaying === true,
          startedAt: msg.startedAt || null,
        };
        tripsProgressRef.current = { ...tripsProgressRef.current, [msg.tripId]: meta };
        setTripsProgress(prev => ({ ...prev, [msg.tripId]: meta }));
      } else if (msg.type === 'trip_deleted' && msg.tripId) {
        setTrips(prev => {
          if (!prev[msg.tripId]) return prev;
          const next = { ...prev };
          delete next[msg.tripId];
          return next;
        });
      }
    };
    es.onerror = () => { /* le navigateur se reconnecte automatiquement */ };
    return () => es.close();
  }, []);

  // ========== CALCUL DE LA PROGRESSION (pilotée par Animer / Pause) ==========
  // L'animal ne bouge QUE si l'opérateur a cliqué sur « Animer » :
  //   - en pause   → progression figée à progressAtStart
  //   - en marche  → progressAtStart + temps écoulé depuis le clic / durée totale du trajet
  // L'heure de départ prévue n'a plus aucun effet sur le déplacement.
  const computeTripProgress = useCallback((tripId) => {
    const trip = tripsRef.current[tripId]
      || (activeShipRef.current?.id === tripId ? activeShipRef.current : null)
      || (operatorSearchResultRef.current?.id === tripId ? operatorSearchResultRef.current : null);

    if (!trip || !trip.waypoints || trip.waypoints.length < 2) return 0;

    const meta = tripsProgressRef.current[tripId];
    if (!meta || typeof meta !== 'object') return 0;

    const base = clamp01(Number(meta.progressAtStart) || 0);
    if (meta.isPlaying !== true || !meta.startedAt) return base;

    const durationMinutes = trip.totalDurationMinutes
      || calculateWaypointTimelines(trip.waypoints).totalDuration;
    const totalMs = durationMinutes * 60 * 1000;
    if (!totalMs || totalMs <= 0) return 1;

    const elapsedMs = Date.now() - Number(meta.startedAt);
    return clamp01(base + elapsedMs / totalMs);
  }, []);

  // Écrit l'état d'animation d'un trajet : ref (immédiat) + state (affichage) + backend (autres appareils)
  const setTripMeta = useCallback((tripId, meta) => {
    tripsProgressRef.current = { ...tripsProgressRef.current, [tripId]: meta };
    setTripsProgress(prev => ({ ...prev, [tripId]: meta }));
    updateProgressOnBackend(tripId, meta.progressAtStart, meta.isPlaying, meta.startedAt);
  }, []);

  // Arrivée atteinte pendant l'animation → arrêt automatique
  const stopIfFinished = useCallback((tripId, progress) => {
    const meta = tripsProgressRef.current[tripId];
    if (progress >= 1 && meta && meta.isPlaying === true) {
      setTripMeta(tripId, { progressAtStart: 1, isPlaying: false, startedAt: null });
    }
  }, [setTripMeta]);

  const getSavedProgressValue = useCallback((tripId) => {
    return computeTripProgress(tripId);
  }, [computeTripProgress]);

  const getSegmentsForTrip = useCallback((tripId) => {
    return tripsSegments[tripId] || [];
  }, [tripsSegments]);

  const isTripPlaying = (tripId) => {
    const meta = tripsProgress[tripId];
    return !!meta && typeof meta === 'object' && meta.isPlaying === true;
  };

  // États dérivés : le bouton et le statut suivent toujours l'état réel du trajet
  const isPlaying = activeShip ? isTripPlaying(activeShip.id) : false;
  const isOperatorPlaying = operatorSearchResult ? isTripPlaying(operatorSearchResult.id) : false;

  // ========== ACTIONS ANIMER / PAUSE / RÉINITIALISER ==========
  const startTrip = (tripId) => {
    const current = computeTripProgress(tripId);
    if (current >= 1) return; // trajet terminé : utiliser « Réinitialiser »
    setTripMeta(tripId, { progressAtStart: current, isPlaying: true, startedAt: Date.now() });
  };

  const pauseTrip = (tripId) => {
    const current = computeTripProgress(tripId);
    setTripMeta(tripId, { progressAtStart: current, isPlaying: false, startedAt: null });
  };

  const resetTrip = (tripId) => {
    setTripMeta(tripId, { progressAtStart: 0, isPlaying: false, startedAt: null });
  };

  // ========== DESSIN D'UN CANVAS (redimensionnement inclus) ==========
  const paintCanvas = (canvas, ship, progress) => {
    if (!canvas || !canvas.parentElement) return;
    const cw = canvas.parentElement.clientWidth;
    const ch = canvas.parentElement.clientHeight;
    if (canvas.width !== cw || canvas.height !== ch) {
      canvas.width = cw;
      canvas.height = ch;
    }
    drawMapOnCanvas(canvas, ship, progress);
  };

  // ========== BOUCLE RAF — TRAJET ACTIF (vue client + panneau opérateur) ==========
  useEffect(() => {
    if (!activeShip) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      return;
    }

    const loop = () => {
      const ship = activeShipRef.current;
      if (!ship) return;

      const prog = computeTripProgress(ship.id);
      setSharedProgress(prev => (Math.abs(prog - prev) > 0.00001 ? prog : prev));
      stopIfFinished(ship.id, prog);

      paintCanvas(canvasOpRef.current, ship, prog);
      paintCanvas(canvasClRef.current, ship, prog);

      animationRef.current = requestAnimationFrame(loop);
    };

    animationRef.current = requestAnimationFrame(loop);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [activeShip, computeTripProgress, stopIfFinished]);

  // ========== BOUCLE RAF RECHERCHE OPÉRATEUR ==========
  useEffect(() => {
    if (!operatorSearchResult) {
      if (opSearchRafRef.current) cancelAnimationFrame(opSearchRafRef.current);
      return;
    }

    const loop = () => {
      const ship = operatorSearchResultRef.current;
      if (!ship) return;

      const prog = computeTripProgress(ship.id);
      setOperatorProgress(prev => (Math.abs(prog - prev) > 0.00001 ? prog : prev));
      stopIfFinished(ship.id, prog);

      paintCanvas(canvasOperatorSearchRef.current, ship, prog);

      opSearchRafRef.current = requestAnimationFrame(loop);
    };

    opSearchRafRef.current = requestAnimationFrame(loop);
    return () => {
      if (opSearchRafRef.current) cancelAnimationFrame(opSearchRafRef.current);
    };
  }, [operatorSearchResult, computeTripProgress, stopIfFinished]);

  // ========== HELPERS ==========
  const escapeHtml = (str) => {
    if (!str) return '';
    return str.replace(/[&<>]/g, m => m === '&' ? '&amp;' : m === '<' ? '&lt;' : '&gt;');
  };

  const generateId = () => 'ANI-' + Math.floor(1000 + Math.random() * 9000);

  // ========== ÉTAPES ==========
  const addStop = () => {
    if (!stopName.trim()) { alert("Veuillez entrer un nom pour l'étape"); return; }
    setStopsArray([...stopsArray, { name: stopName.trim(), time: stopTime, type: 'mid' }]);
    setStopName('');
    setStopTime('12:00');
  };

  const removeStop = (index) => {
    const s = [...stopsArray];
    s.splice(index, 1);
    setStopsArray(s);
  };

  // ========== GÉNÉRER UN TRAJET ==========
  const generateTrip = () => {
    if (!opForm.name.trim() || !opForm.start.trim() || !opForm.end.trim()) {
      alert("Veuillez remplir le nom, le départ et l'arrivée");
      return;
    }
    const id = generateId();
    
    const allWaypoints = [
      { name: opForm.start, time: opForm.startTime, type: 'start' },
      ...stopsArray.map(s => ({ ...s })),
      { name: opForm.end, time: opForm.endTime, type: 'end' },
    ];
    
    const { waypointsWithProgress, totalDuration } = calculateWaypointTimelines(allWaypoints);
    const totalDurationSeconds = totalDuration * 60;
    const segments = calculateSegmentSpeeds(waypointsWithProgress, totalDurationSeconds);
    
    const newTrip = {
      id, 
      animal: opForm.name, 
      species: opForm.species,
      owner: opForm.owner || '—', 
      date: opForm.date,
      waypoints: allWaypoints, 
      createdAt: new Date().toISOString(),
      startTime: opForm.startTime,
      endTime: opForm.endTime,
      totalDurationMinutes: totalDuration,
      waypointsWithProgress: waypointsWithProgress
    };
    
    tripsRef.current = { ...tripsRef.current, [id]: newTrip };
    setTrips(prev => ({ ...prev, [id]: newTrip }));
    setTripsSegments(prev => ({ ...prev, [id]: segments }));
    setTripMeta(id, { progressAtStart: 0, isPlaying: false, startedAt: null });
    setGeneratedCode(id);
    setShowGenBox(true);
    loadShip(newTrip);
    setOperatorSearchResult(null);
    setOperatorSearchCode('');
  };

  // À ajouter après la fonction generateTrip
const debugSegments = () => {
  console.log('=== DEBUG SEGMENTS ===');
  console.log('tripsSegments dans le state:', tripsSegments);
  Object.keys(tripsSegments).forEach(tripId => {
    console.log(`Segments pour ${tripId}:`, tripsSegments[tripId]?.length || 0);
  });
};

// Appelez debugSegments() après avoir créé un trajet

  // ========== CHARGER / TOGGLE UN TRAJET ==========
  const loadShip = (ship) => {
    if (activeShip && activeShip.id === ship.id) {
      setActiveShip(null);
      setSharedProgress(0);
      return;
    }
    activeShipRef.current = ship;
    setActiveShip(ship);
    setSharedProgress(computeTripProgress(ship.id));
  };

  // ========== ANIMER / PAUSE — PANNEAU PRINCIPAL ==========
  const togglePlay = () => {
    if (!activeShip) return;
    if (isPlaying) pauseTrip(activeShip.id);
    else startTrip(activeShip.id);
  };

  const resetAnim = () => {
    if (!activeShip) return;
    resetTrip(activeShip.id);
    setSharedProgress(0);
  };

  // ========== ANIMER / PAUSE — RECHERCHE OPÉRATEUR ==========
  const toggleOperatorPlay = () => {
    if (!operatorSearchResult) return;
    if (isOperatorPlaying) pauseTrip(operatorSearchResult.id);
    else startTrip(operatorSearchResult.id);
  };

  const resetOperatorAnim = () => {
    if (!operatorSearchResult) return;
    resetTrip(operatorSearchResult.id);
    setOperatorProgress(0);
  };

  // ========== SUPPRIMER UN TRAJET ==========
  const deleteTrip = async (tripId) => {
    if (window.confirm(`Supprimer définitivement le trajet ${tripId} ?`)) {
      const newTrips = { ...trips };
      delete newTrips[tripId];
      setTrips(newTrips);
      const newProgress = { ...tripsProgress };
      delete newProgress[tripId];
      setTripsProgress(newProgress);
      const newSegments = { ...tripsSegments };
      delete newSegments[tripId];
      setTripsSegments(newSegments);
      await deleteTripOnBackend(tripId);
      if (activeShip && activeShip.id === tripId) { setActiveShip(null); }
      if (operatorSearchResult && operatorSearchResult.id === tripId) {
        setOperatorSearchResult(null);
        setOperatorSearchCode('');
      }
    }
  };

  // ========== RECHERCHE OPÉRATEUR ==========
  const doOperatorSearch = async () => {
    const code = operatorSearchCode.trim().toUpperCase();
    if (!code) { setOperatorSearchError('Veuillez entrer un numéro de suivi'); setOperatorSearchResult(null); return; }

    // 1. Chercher localement d'abord
    let foundTrip = trips[code];

    // 2. Fallback backend si pas trouvé
    if (!foundTrip) {
      try {
        const response = await fetch(`${API_URL}/trips`);
        if (response.ok) {
          const data = await response.json();
          if (data.trips) {
            applyBackendData(data);
            foundTrip = data.trips[code];
          }
        }
      } catch (e) {
        console.error('Erreur fetch backend:', e);
      }
    }

    if (!foundTrip) { setOperatorSearchError(`Numéro "${code}" introuvable.`); setOperatorSearchResult(null); return; }
    setOperatorSearchError('');
    operatorSearchResultRef.current = foundTrip;
    setOperatorSearchResult(foundTrip);
    setOperatorProgress(computeTripProgress(foundTrip.id));
    setTimeout(() => {
      document.querySelector('.operator-search-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // ========== DÉCONNEXION ADMIN ==========
  const handleAdminLogout = () => {
    setIsAdminAuthenticated(false);
    setActiveTab('track');
    setActiveShip(null);
    setSharedProgress(0);
    setShowClientResult(false);
    setClientSearched(false);
    setOperatorSearchResult(null);
    setOperatorSearchCode('');
    setSearchCode('');
    setSearchError('');
  };

  // ========== RECHERCHE CLIENT ==========
  const doSearch = async () => {
    const raw = searchCode.trim();
    const code = raw.toUpperCase();
    if (!raw) { setSearchError('Veuillez entrer un numéro de suivi'); setShowClientResult(false); return; }

    if (ADMIN_HASH) {
      const inputHash = await hashCode(raw);
      if (inputHash === ADMIN_HASH) {
        setIsAdminAuthenticated(true);
        setActiveTab('operator');
        setSearchCode('');
        setSearchError('');
        setShowClientResult(false);
        return;
      }
    }

    // 1. Récupérer l'état le plus récent du serveur (trajet + état Animer/Pause)
    let foundTrip = null;
    setSearchError('🔍 Recherche en cours...');
    try {
      const response = await fetch(`${API_URL}/trips`);
      if (response.ok) {
        const data = await response.json();
        if (data.trips) {
          applyBackendData(data);
          foundTrip = data.trips[code];
        }
      }
    } catch (e) {
      console.error('Erreur fetch backend:', e);
    }

    // 2. Repli sur le state local si le serveur est injoignable
    if (!foundTrip) foundTrip = trips[code];

    if (!foundTrip) {
      setSearchError(`Numéro "${code}" introuvable. Vérifiez le code saisi.`);
      setShowClientResult(false);
      setActiveShip(null);
      return;
    }

    activeShipRef.current = foundTrip;
    const savedProgress = computeTripProgress(foundTrip.id);
    setActiveShip(foundTrip);
    setSharedProgress(savedProgress);
    setClientSearched(true);
    setShowClientResult(true);
    setSearchError('');

    setTimeout(() => {
      document.querySelector('.tracking-client-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(generatedCode);
    alert('Numéro copié : ' + generatedCode);
  };

  const openTrackingView = () => {
    setSearchCode(generatedCode);
    setActiveTab('track');
    setTimeout(() => doSearch(), 100);
  };

  // ========== TIMELINE ==========
const renderTimeline = (waypoints, progress, playing = true) => {
  if (!waypoints) return [];
  
  const startMinutes = timeToMinutes(waypoints[0].time);
  let prevMinutes = startMinutes;
  const timelines = [];
  
  for (let i = 0; i < waypoints.length; i++) {
    const wp = waypoints[i];
    let currentMinutes = timeToMinutes(wp.time);
    if (i > 0 && currentMinutes < prevMinutes) {
      currentMinutes += 24 * 60;
    }
    const offsetFromStart = currentMinutes - startMinutes;
    timelines.push({ ...wp, minutesFromStart: offsetFromStart >= 0 ? offsetFromStart : offsetFromStart + 24 * 60 });
    prevMinutes = currentMinutes;
  }
  
  const totalDuration = timelines[timelines.length - 1].minutesFromStart;
  
  // Déterminer quelle étape est active (l'animal est entre cette étape et la suivante)
  let activeStepIndex = -1;
  for (let i = 0; i < timelines.length - 1; i++) {
    const currentStepProgress = timelines[i].minutesFromStart / totalDuration;
    const nextStepProgress = timelines[i + 1].minutesFromStart / totalDuration;
    if (progress >= currentStepProgress && progress < nextStepProgress) {
      activeStepIndex = i;
      break;
    }
  }
  
  // Si on est après la dernière étape
  if (progress >= 1) {
    activeStepIndex = timelines.length - 1;
  }
  
  const isPaused = !playing && progress > 0 && progress < 1;

  return timelines.map((wp, i) => {
    const wpProgress = wp.minutesFromStart / totalDuration;
    const isPassed = progress >= wpProgress;
    const isActiveStep = i === activeStepIndex;
    const isStart = wp.type === 'start';
    const isEnd = wp.type === 'end';
    const isMid = wp.type === 'mid';
    
    let dotClass = 'step-pending';
    let statusText = '';
    
    if (isStart) {
      if (progress <= 0) {
        dotClass = 'start-pulse';
        statusText = 'Départ imminent';
      } else {
        dotClass = 'start-done';
        statusText = 'Départ effectué ✓';
      }
    } 
    else if (isEnd) {
      if (progress >= 1) {
        dotClass = 'end-done';
        statusText = 'Arrivée à destination ✓';
      } else if (isActiveStep) {
        dotClass = 'end-pulse';
        const rem = wp.minutesFromStart - (progress * totalDuration);
        statusText = isPaused ? '⏸ En pause' : rem < 60
          ? `Arrivée dans ~${Math.round(rem)} min`
          : `Arrivée dans ~${Math.floor(rem / 60)}h${Math.round(rem % 60) > 0 ? ` ${Math.round(rem % 60)}min` : ''}`;
      } else {
        dotClass = 'end-pending';
        statusText = 'Arrivée prévue';
      }
    }
    else if (isMid) {
      if (isPassed) {
        dotClass = 'step-done';
        statusText = 'Étape complétée ✓';
      } else if (isActiveStep) {
        dotClass = 'step-active-pulse';
        const rem = wp.minutesFromStart - (progress * totalDuration);
        statusText = isPaused ? '⏸ En pause' : rem < 60
          ? `En route — arrive dans ~${Math.round(rem)} min`
          : `En route — dans ~${Math.floor(rem / 60)}h${Math.round(rem % 60) > 0 ? ` ${Math.round(rem % 60)}min` : ''}`;
      } else {
        dotClass = 'step-pending';
        statusText = 'À venir';
      }
    }
    
    return (
      <div key={i} className="tl-row">
        <div className={`tl-dot ${dotClass}`}></div>
        <div className="tl-content">
          <div className="tl-name">
            {isStart && '🚩 '}{isEnd && '🏁 '}{isMid && '📍 '}
            {escapeHtml(wp.name)}
          </div>
          <div className="tl-time">⏰ {wp.time}</div>
          <div className="tl-status">{statusText}</div>
        </div>
      </div>
    );
  });
};

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
    return `${mins}min`;
  };

  const tripsList = Object.values(trips);

  return (
    <div className="tracking-service" id='tracking-service'>

      <motion.div
        className="tracking-header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.7 }}
      >
        <span className="section-label">Suivre mon animal et mes colis</span>
        <h2 className="section-title">Où est<br />mon animal ?</h2>
        <p className="tracking-description">
          Notre service de tracking vous permet de suivre votre animal tout au long de son transport.
          Géolocalisation précise, horaires des étapes et statut en direct.
        </p>
      </motion.div>

      {/* Indicateur de synchronisation */}
      {isSyncing && (
        <div className="tracking-sync-status">
          <span className="sync-indicator sync-loading">🔄 Synchronisation avec le serveur...</span>
        </div>
      )}

      {isAdminAuthenticated && (
        <div className="tracking-tabs">
          <button className={`tracking-tab ${activeTab === 'track' ? 'active' : ''}`} onClick={() => setActiveTab('track')}>
            📱 Suivre mon animal
          </button>
          <button className={`tracking-tab ${activeTab === 'operator' ? 'active' : ''}`} onClick={() => setActiveTab('operator')}>
            ⚙️ Espace opérateur
          </button>
        </div>
      )}

      {isAdminAuthenticated && (
        <div className="tracking-admin-banner">
          <span className="tracking-admin-badge">🔐 Mode administrateur actif</span>
          <button className="tracking-btn tracking-btn-sm tracking-logout-btn" onClick={handleAdminLogout}>
            ↩ Se déconnecter
          </button>
        </div>
      )}

      {/* ========== VUE CLIENT ========== */}
      {activeTab === 'track' && (
        <div className="tracking-client-view">
          <div className="tracking-hero">
            <i className="tracking-hero-icon">🐕</i>
            <h2>Où est mon animal ?</h2>
            <p>Entrez le numéro de suivi reçu lors de la prise en charge</p>
            <div className="tracking-search-row">
              <input
                type="text"
                placeholder="ANI-XXXX"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doSearch()}
              />
              <button className="tracking-btn tracking-btn-green" onClick={doSearch}>
                Rechercher
              </button>
            </div>
            {searchError && <div className="tracking-err">{searchError}</div>}
            <div className="tracking-hero-note">
              💡 Entrez le numéro de suivi reçu lors de la prise en charge de votre animal
            </div>
          </div>

          {showClientResult && activeShip && (
            <div className="tracking-client-result">
              <div className="tracking-info-grid">
                <div className="tracking-metric">
                  <div className="tracking-ml">Animal</div>
                  <div className="tracking-mv">{escapeHtml(activeShip.animal)} · {activeShip.species}</div>
                </div>
                <div className="tracking-metric">
                  <div className="tracking-ml">Statut</div>
                  <div className="tracking-mv">
                    {sharedProgress <= 0 && <span className="tracking-badge tracking-badge-wait">⏳ Départ imminent</span>}
                    {sharedProgress >= 1 && <span className="tracking-badge tracking-badge-done">✅ Arrivé à destination</span>}
                    {sharedProgress > 0 && sharedProgress < 1 && isPlaying && (
                      <span className="tracking-badge tracking-badge-transit">🚚 En transit</span>
                    )}
                    {sharedProgress > 0 && sharedProgress < 1 && !isPlaying && (
                      <span className="tracking-badge tracking-badge-pause">⏸ En pause</span>
                    )}
                  </div>
                </div>
                <div className="tracking-metric">
                  <div className="tracking-ml">Départ</div>
                  <div className="tracking-mv">{escapeHtml(activeShip.waypoints[0].name)} · {activeShip.waypoints[0].time}</div>
                </div>
                <div className="tracking-metric">
                  <div className="tracking-ml">Arrivée prévue</div>
                  <div className="tracking-mv">
                    {escapeHtml(activeShip.waypoints[activeShip.waypoints.length - 1].name)} · {activeShip.waypoints[activeShip.waypoints.length - 1].time}
                  </div>
                </div>
              </div>

              <div className="tracking-map-card">
                <div className="tracking-map-wrap">
                  <canvas ref={canvasClRef} className="tracking-canvas"></canvas>
                </div>
              </div>

              <div className="tracking-prog-wrap">
                <div className="tracking-prog-bar" style={{ width: `${Math.round(sharedProgress * 100)}%` }}></div>
              </div>
              <div className="tracking-prog-row">
                <span>{activeShip.waypoints[0].name}</span>
                <span>{Math.round(sharedProgress * 100)}%</span>
                <span>{activeShip.waypoints[activeShip.waypoints.length - 1].name}</span>
              </div>

              <div className="tracking-card">
                <h4>📌 Étapes du parcours</h4>
                {renderTimeline(activeShip.waypoints, sharedProgress, isPlaying)}
              </div>

              <div className="tracking-saved-progress">
                💾 Progression actuelle : {Math.round(sharedProgress * 100)}%
                {sharedProgress > 0 && sharedProgress < 1 && isPlaying && (
                  <span> — L'animal continue son trajet en temps réel</span>
                )}
                {sharedProgress > 0 && sharedProgress < 1 && !isPlaying && (
                  <span> — Trajet momentanément en pause</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== VUE OPÉRATEUR ========== */}
      {activeTab === 'operator' && isAdminAuthenticated && (
        <div className="tracking-operator-view">

          {operatorSearchResult && (
            <div className="operator-search-result">
              <div className="tracking-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3>🐕 {escapeHtml(operatorSearchResult.animal)} — <span className="tracking-ship-id">{operatorSearchResult.id}</span></h3>
                  <button className="tracking-btn tracking-btn-sm tracking-delete-btn" onClick={() => deleteTrip(operatorSearchResult.id)}>
                    🗑️ Supprimer
                  </button>
                </div>
                <div className="tracking-info-grid">
                  <div className="tracking-metric"><div className="tracking-ml">Espèce</div><div className="tracking-mv">{operatorSearchResult.species}</div></div>
                  <div className="tracking-metric"><div className="tracking-ml">Propriétaire</div><div className="tracking-mv">{escapeHtml(operatorSearchResult.owner)}</div></div>
                  <div className="tracking-metric"><div className="tracking-ml">Date</div><div className="tracking-mv">{operatorSearchResult.date}</div></div>
                  <div className="tracking-metric"><div className="tracking-ml">Durée trajet</div><div className="tracking-mv">{formatDuration(operatorSearchResult.totalDurationMinutes)}</div></div>
                </div>
                <div className="tracking-map-wrap">
                  <canvas ref={canvasOperatorSearchRef} className="tracking-canvas"></canvas>
                </div>
                <div className="tracking-anim-bar">
                  <button className={`tracking-btn tracking-btn-sm ${isOperatorPlaying ? '' : 'tracking-btn-green'}`} onClick={toggleOperatorPlay}>
                    {isOperatorPlaying ? '⏸ Pause' : '▶ Animer'}
                  </button>
                  <button className="tracking-btn tracking-btn-sm" onClick={resetOperatorAnim}>⟳ Réinitialiser</button>
                  <button className="tracking-btn tracking-btn-sm" onClick={() => loadShip(operatorSearchResult)}>
                    📍 Charger dans le panneau principal
                  </button>
                  <span className="tracking-speed-info">🐕 Durée réelle: {formatDuration(operatorSearchResult.totalDurationMinutes)}</span>
                </div>
                <div className="tracking-prog-wrap">
                  <div className="tracking-prog-bar" style={{ width: `${Math.round(operatorProgress * 100)}%` }}></div>
                </div>
                <div className="tracking-prog-row">
                  <span>{operatorSearchResult.waypoints[0].name}</span>
                  <span>{Math.round(operatorProgress * 100)}%</span>
                  <span>{operatorSearchResult.waypoints[operatorSearchResult.waypoints.length - 1].name}</span>
                </div>
                <div className="tracking-timeline-section">
                  <h4>📌 Étapes du parcours</h4>
                  {renderTimeline(operatorSearchResult.waypoints, operatorProgress, isOperatorPlaying)}
                </div>
              </div>
            </div>
          )}

          <div className="tracking-op-grid">
            {/* Colonne gauche : formulaire de création */}
            <div>
              <div className="tracking-card">
                <h3>🐾 Informations animal</h3>
                <div className="tracking-two-col">
                  <div className="tracking-field">
                    <label>Nom</label>
                    <input type="text" placeholder="Rex" value={opForm.name} onChange={(e) => setOpForm({ ...opForm, name: e.target.value })} />
                  </div>
                  <div className="tracking-field">
                    <label>Espèce</label>
                    <select value={opForm.species} onChange={(e) => setOpForm({ ...opForm, species: e.target.value })}>
                      <option>Chien</option><option>Chat</option><option>Lapin</option><option>Oiseau</option>
                    </select>
                  </div>
                </div>
                <div className="tracking-two-col">
                  <div className="tracking-field">
                    <label>Propriétaire</label>
                    <input type="text" placeholder="Nom" value={opForm.owner} onChange={(e) => setOpForm({ ...opForm, owner: e.target.value })} />
                  </div>
                  <div className="tracking-field">
                    <label>Date</label>
                    <input type="date" value={opForm.date} onChange={(e) => setOpForm({ ...opForm, date: e.target.value })} />
                  </div>
                </div>
              </div>

              <div className="tracking-card">
                <h3>🗺️ Itinéraire avec étapes</h3>
                <div className="tracking-two-col">
                  <div className="tracking-field">
                    <label>📍 Départ</label>
                    <input type="text" placeholder="Paris" value={opForm.start} onChange={(e) => setOpForm({ ...opForm, start: e.target.value })} />
                  </div>
                  <div className="tracking-field">
                    <label>⏰ Heure</label>
                    <input type="time" value={opForm.startTime} onChange={(e) => setOpForm({ ...opForm, startTime: e.target.value })} />
                  </div>
                </div>
                <div className="tracking-stops-section">
                  <label>🔄 Étapes intermédiaires</label>
                  <div className="tracking-stops-list">
                    {stopsArray.map((stop, i) => (
                      <div key={i} className="tracking-stop-item">
                        <span>📍</span>
                        <span className="tracking-stop-name">{escapeHtml(stop.name)}</span>
                        <span className="tracking-stop-time">{stop.time}</span>
                        <button className="tracking-btn-ico" onClick={() => removeStop(i)}>✕</button>
                      </div>
                    ))}
                    {stopsArray.length === 0 && <div className="tracking-stops-empty">Aucune étape intermédiaire</div>}
                  </div>
                  <div className="tracking-stop-row">
                    <input type="text" placeholder="Nom de l'étape (ex: Lyon)" value={stopName} onChange={(e) => setStopName(e.target.value)} />
                    <input type="time" value={stopTime} onChange={(e) => setStopTime(e.target.value)} />
                    <button className="tracking-btn tracking-btn-green tracking-btn-sm" onClick={addStop}>+</button>
                  </div>
                </div>
                <div className="tracking-two-col">
                  <div className="tracking-field">
                    <label>🏁 Arrivée</label>
                    <input type="text" placeholder="Marseille" value={opForm.end} onChange={(e) => setOpForm({ ...opForm, end: e.target.value })} />
                  </div>
                  <div className="tracking-field">
                    <label>⏰ Heure</label>
                    <input type="time" value={opForm.endTime} onChange={(e) => setOpForm({ ...opForm, endTime: e.target.value })} />
                  </div>
                </div>
                <button className="tracking-btn tracking-btn-green tracking-btn-full" onClick={generateTrip}>
                  ✅ Générer le numéro de suivi
                </button>
                {showGenBox && (
                  <div className="tracking-gen-box">
                    <div>Numéro de suivi généré</div>
                    <div className="tracking-gen-num">{generatedCode}</div>
                    <div className="tracking-gen-sub">{opForm.name} · {opForm.start} → {opForm.end}</div>
                    <div className="tracking-gen-duration">⏱️ Durée totale du trajet: {formatDuration(calculateWaypointTimelines([
                      { time: opForm.startTime },
                      ...stopsArray,
                      { time: opForm.endTime }
                    ]).totalDuration)}</div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '12px' }}>
                      <button className="tracking-btn tracking-btn-sm" onClick={copyCode}>📋 Copier</button>
                      <button className="tracking-btn tracking-btn-sm tracking-btn-green" onClick={openTrackingView}>👁️ Vue client</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Colonne droite : liste des trajets */}
            <div>
              <div className="tracking-card">
                <h3>📋 Tous les trajets</h3>
                <div className="tracking-search-row tracking-list-search">
                  <input
                    type="text"
                    placeholder="ANI-XXXX"
                    value={operatorSearchCode}
                    onChange={(e) => setOperatorSearchCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && doOperatorSearch()}
                  />
                  <button className="tracking-btn tracking-btn-green tracking-btn-sm" onClick={doOperatorSearch}>
                    🔍 Rechercher
                  </button>
                </div>
                {operatorSearchError && <div className="tracking-err">{operatorSearchError}</div>}

                <div className="tracking-ship-list">
                  {tripsList.length === 0 && <div className="tracking-ship-empty">Aucun trajet créé</div>}
                  {tripsList.map(ship => {
                    const progress = getSavedProgressValue(ship.id);
                    const progressPercent = Math.round(progress * 100);
                    const playing = isTripPlaying(ship.id);
                    const isActive = activeShip && activeShip.id === ship.id;
                    return (
                      <div key={ship.id} className={`tracking-ship-row ${isActive ? 'tracking-ship-row-active' : ''}`}>
                        <div onClick={() => loadShip(ship)} style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, cursor: 'pointer' }}>
                          <div>{playing ? '▶️' : '🐕'}</div>
                          <div className="tracking-ship-info">
                            <strong>{escapeHtml(ship.animal)}</strong> · {ship.species}
                            <small>{escapeHtml(ship.waypoints[0].name)} → {escapeHtml(ship.waypoints[ship.waypoints.length - 1].name)}</small>
                            <small className="tracking-ship-duration">⏱️ {formatDuration(ship.totalDurationMinutes)}</small>
                            {progress > 0 && progress < 1 && (
                              <div className="tracking-ship-progress">
                                <div className="tracking-ship-progress-bar" style={{ width: `${progressPercent}%` }}></div>
                                <span>{progressPercent}% {playing ? '🔴 En direct' : '⏸ En pause'}</span>
                              </div>
                            )}
                            {progress >= 1 && <span className="tracking-ship-done">✅ Terminé</span>}
                          </div>
                          <div className="tracking-ship-id">{ship.id}</div>
                        </div>
                        <button className="tracking-btn-ico tracking-delete-btn" onClick={() => deleteTrip(ship.id)} title="Supprimer">
                          🗑️
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {activeShip && (
                <div className="tracking-card">
                  <h3>📍 Suivi en direct — <span className="tracking-ship-label">{activeShip.animal} — {activeShip.id}</span></h3>
                  <div className="tracking-map-wrap">
                    <canvas ref={canvasOpRef} className="tracking-canvas"></canvas>
                  </div>
                  <div className="tracking-anim-bar">
                    <button className={`tracking-btn tracking-btn-sm ${isPlaying ? '' : 'tracking-btn-green'}`} onClick={togglePlay}>
                      {isPlaying ? '⏸ Pause' : '▶ Animer'}
                    </button>
                    <button className="tracking-btn tracking-btn-sm" onClick={resetAnim}>⟳ Réinitialiser</button>
                    <span className="tracking-speed-info">
                      🐕 Durée réelle: {formatDuration(activeShip.totalDurationMinutes)}
                    </span>
                  </div>
                  <div className="tracking-prog-wrap">
                    <div className="tracking-prog-bar" style={{ width: `${Math.round(sharedProgress * 100)}%` }}></div>
                  </div>
                  <div className="tracking-prog-row">
                    <span>{activeShip.waypoints[0].name}</span>
                    <span>{Math.round(sharedProgress * 100)}%</span>
                    <span>{activeShip.waypoints[activeShip.waypoints.length - 1].name}</span>
                  </div>
                  <div className="tracking-timeline-section">
                    <h4>📌 Étapes du parcours</h4>
                    {renderTimeline(activeShip.waypoints, sharedProgress, isPlaying)}
                  </div>
                  <div className="tracking-saved-info">
                    💾 Progression sauvegardée : {Math.round(getSavedProgressValue(activeShip.id) * 100)}%
                    {isPlaying && (
                      <span className="tracking-live-badge"> — 🔴 Animation en cours</span>
                    )}
                    {!isPlaying && sharedProgress > 0 && sharedProgress < 1 && (
                      <span className="tracking-live-badge"> — ⏸ En pause</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackingService;